/**
 * 建筑(方案 C5):随纪元累加、永不拆除;从地面 scaleY 生长(backOut)+ 根部尘土;
 * 窗洞夜间亮 ember 灯(alpha 随 1−ambient)。剪影色派生自 SkyState。
 */
import { Container, Graphics, Sprite } from 'pixi.js';
import { Stage, LW, GROUND_Y } from '../engine/Stage';
import { sky } from '../engine/SkyRenderer';
import { eventPool } from '../engine/ParticlePool';
import { TEX } from '../engine/Tex';
import { P } from '../engine/Palette';
import { easing } from '../engine/Easing';
import { rand } from '../engine/util';

interface Win { x: number; y: number }
interface Bld {
  epoch: number; node: Container; g: Graphics; winSprites: Sprite[]; wins: Win[];
  draw: (g: Graphics, sil: number) => void; grow: number; growV: number; revealed: boolean; baseY: number;
}

const CX = LW * 0.5;

export class Buildings {
  private list: Bld[] = [];
  private layer: Container;
  private revealedEpoch = 0;
  private surfaceYAt: (x: number) => number;

  constructor(stage: Stage, surfaceYAt: (x: number) => number) {
    this.layer = stage.layers.mainIsland;
    this.surfaceYAt = surfaceYAt;
    this.build();
  }

  private add(epoch: number, x: number, wins: Win[], draw: (g: Graphics, sil: number) => void) {
    const node = new Container();
    const baseY = this.surfaceYAt(x) + 2;
    node.position.set(x, baseY);
    node.scale.y = 0.001; node.visible = false;
    const g = new Graphics();
    const winSprites = wins.map(() => {
      const s = new Sprite(TEX.glow()); s.anchor.set(0.5); s.blendMode = 'add'; s.tint = P.ember; s.alpha = 0; s.scale.set(0.06);
      return s;
    });
    node.addChild(g, ...winSprites);
    this.layer.addChild(node);
    this.list.push({ epoch, node, g, winSprites, wins, draw, grow: 0, growV: 0, revealed: false, baseY });
  }

  private build() {
    // 生命纪:帐篷×3
    for (const x of [CX - 360, CX - 300, CX - 240]) {
      this.add(2, x, [{ x: 0, y: -18 }], (g, sil) => {
        g.moveTo(-16, 0).lineTo(0, -36).lineTo(16, 0).closePath().fill(sil);
      });
    }
    // 青铜纪:木屋×5 + 农田
    for (const x of [CX - 400, CX - 330, CX - 150, CX + 150, CX + 260]) {
      this.add(3, x, [{ x: -6, y: -30 }, { x: 7, y: -30 }], (g, sil) => {
        g.roundRect(-15, -40, 30, 40, 4).fill(sil);
        g.moveTo(-18, -40).lineTo(0, -54).lineTo(18, -40).closePath().fill(sil);
      });
    }
    this.add(3, CX + 20, [], (g, sil) => { // 农田刻线
      for (let i = 0; i < 5; i++) g.moveTo(-70 + i * 34, 2).lineTo(-60 + i * 34, 8).stroke({ width: 1, color: sil, alpha: 0.5 });
    });
    // 白银纪:双层塔楼×2 + 灯柱×6
    for (const x of [CX - 470, CX + 470]) {
      this.add(5, x, [{ x: -8, y: -30 }, { x: 6, y: -55 }, { x: -6, y: -80 }, { x: 7, y: -95 }], (g, sil) => {
        g.roundRect(-20, -110, 40, 110, 5).fill(sil);
        g.moveTo(-24, -110).lineTo(0, -128).lineTo(24, -110).closePath().fill(sil);
      });
    }
    for (const x of [CX - 200, CX - 90, CX + 90, CX + 200, CX + 300, CX - 300]) {
      this.add(5, x, [{ x: 0, y: -60 }], (g, sil) => { g.rect(-2, -58, 4, 58).fill(sil); });
    }
    // 黄金纪:万币神像(永久;D15 播仪式)。
    // 重设计:旧版"矩形底座+圆角矩形+圆头" = 雪人墓碑,与"万币铸神"的分量完全不配。
    // 新造型 = 兜帽先知:三级莲座 → 一条闭合贝塞尔画出裙摆-收腰-展肩-帽尖的连续轮廓,
    // 两片垂袖分离出层次;头后 divine 光环(bloom 会替它发光),胸前捧一枚 ember 火珠——
    // 与岛心火种同源,"神像捧着的就是这个世界的火种"。夜晚火珠位挂 win 灯,自己会亮。
    this.add(6, CX, [{ x: 0, y: -96 }], (g, sil) => {
      // 三级莲座(叠级椭圆,比矩形底座多一层"被供奉"的仪式感)
      g.ellipse(0, -5, 44, 9).fill(sil);
      g.ellipse(0, -13, 32, 7).fill(sil);
      g.ellipse(0, -20, 22, 6).fill(sil);
      // 长袍身躯+兜帽:单条连续贝塞尔(§B3:主体轮廓禁折线)
      g.moveTo(-30, -18);
      g.bezierCurveTo(-26, -60, -16, -86, -17, -112);   // 左袍侧:裙摆→收腰
      g.bezierCurveTo(-22, -128, -14, -150, 0, -160);   // 左肩→帽尖
      g.bezierCurveTo(14, -150, 22, -128, 17, -112);    // 帽尖→右肩
      g.bezierCurveTo(16, -86, 26, -60, 30, -18);       // 右袍侧
      g.closePath().fill(sil);
      // 垂袖×2:与身躯轮廓分离一线,剪影出层次
      g.moveTo(-17, -104);
      g.bezierCurveTo(-34, -96, -40, -70, -34, -46);
      g.bezierCurveTo(-28, -58, -22, -80, -14, -92);
      g.closePath().fill(sil);
      g.moveTo(17, -104);
      g.bezierCurveTo(34, -96, 40, -70, 34, -46);
      g.bezierCurveTo(28, -58, 22, -80, 14, -92);
      g.closePath().fill(sil);
      // 头后光环:divine 细环,经 bloom 即为圣光
      g.circle(0, -138, 26).stroke({ width: 2.5, color: P.divine, alpha: 0.55 });
      // 捧火:胸前 ember 火珠(实心亮核+一圈暗晕)
      g.circle(0, -96, 9).fill({ color: P.ember, alpha: 0.35 });
      g.circle(0, -96, 5.5).fill(P.emberHi);
    });
    // 星海纪:光之翼×2(半透明 divine,缓慢扇动 → 用 winSprites 承载翼形近似)
    for (const side of [-1, 1]) {
      this.add(7, CX + side * 120, [], (g, sil) => {
        g.moveTo(0, -60);
        g.bezierCurveTo(side * 120, -140, side * 220, -90, side * 180, -20);
        g.bezierCurveTo(side * 120, -50, side * 60, -40, 0, -60);
        g.closePath().fill({ color: P.divine, alpha: 0.18 });
        void sil;
      });
    }
  }

  /** 神像单独揭示(D15 仪式成果)。修上轮遗留 P1:低纪元触发神像落成时,
   *  12 秒仪式演完什么都没出现——仪式必须有可见成果,否则是"空气神迹"。 */
  revealStatue() {
    const b = this.list.find((x) => x.epoch === 6);
    if (b && !b.revealed) { b.revealed = true; b.node.visible = true; b.growV = -0.1; }
  }

  setEpoch(idx: number) {
    if (idx <= this.revealedEpoch) { this.revealedEpoch = Math.max(this.revealedEpoch, idx); this.revealNow(); return; }
    this.revealedEpoch = idx;
    this.revealNow(true);
  }

  private revealNow(animate = false) {
    let stagger = 0;
    for (const b of this.list) {
      if (b.epoch <= this.revealedEpoch && !b.revealed) {
        b.revealed = true; b.node.visible = true;
        if (animate) { b.growV = -stagger; stagger += 0.3; } else { b.grow = 1; b.node.scale.y = 1; }
      }
    }
  }

  update(dt: number) {
    const sil = sky.silhouette;
    const night = 1 - sky.state.ambient;
    for (const b of this.list) {
      if (!b.revealed) continue;
      // 生长:延迟 → backOut 到 1.1 → 回落 1
      if (b.grow < 1) {
        if (b.growV < 0) { b.growV += dt; if (b.growV >= 0) this.puff(b); }
        else {
          b.grow = Math.min(1, b.grow + dt / 1.2);
          const e = easing.backOut(b.grow);
          b.node.scale.y = e;
        }
      }
      b.g.clear();
      b.draw(b.g, sil);
      b.winSprites.forEach((s, i) => {
        const w = b.wins[i]; s.position.set(w.x, w.y);
        s.alpha = b.grow * (0.25 + night * 0.75);
      });
    }
  }

  private puff(b: Bld) {
    for (let i = 0; i < 8; i++) {
      eventPool?.spawn({
        x: b.node.x + rand(-16, 16), y: b.baseY, vx: rand(-40, 40), vy: rand(-10, -40),
        life: 0.8, scale: 0.03, tint: sky.silhouette, alpha: 0.6, texture: TEX.glow(), fade: 'quad',
      });
    }
  }
}
